
import { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { ApiTranscriptionData } from '@/types/sections';
import { useNavigate } from 'react-router-dom';

interface UseConsultationFlowProps {
  onMobileMenuClose?: () => void;
  onFileTranscriptionComplete?: (transcriptions: ApiTranscriptionData[], title: string) => void;
  template?: any;
  onMicrophoneNotFound?: () => void;
  currentAnalysisId?: number; // Añadir el ID del análisis actual
}

export const useConsultationFlow = ({
  onMobileMenuClose,
  onFileTranscriptionComplete,
  template,
  onMicrophoneNotFound,
  currentAnalysisId
}: UseConsultationFlowProps) => {
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [consultationDialogOpen, setConsultationDialogOpen] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [countdownKey, setCountdownKey] = useState(0);
  const [isContinuation, setIsContinuation] = useState(false);
  const [microphoneErrorOpen, setMicrophoneErrorOpen] = useState(false);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [isNewConsultation, setIsNewConsultation] = useState(true); // Nuevo estado para diferenciar
  const [shouldCreateAnalysis, setShouldCreateAnalysis] = useState(false); // NUEVO: controlar creación de análisis
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const resetAllStates = () => {
    console.log('useConsultationFlow: Resetting all states to initial');
    setShowContinueButton(false);
    setConsultationDialogOpen(false);
    setIsRecording(false);
    setShowCountdown(false);
    setIsContinuation(false);
    setMicrophoneErrorOpen(false);
    setFileUploadModalOpen(false);
    setIsNewConsultation(true);
    setShouldCreateAnalysis(false); // NUEVO: resetear flag de creación
    // Incrementar la key para forzar reinicio del CountdownOverlay
    setCountdownKey(prev => prev + 1);
  };

  const handleMicrophoneNotFound = () => {
    console.log('🎤 [CONSULTATION_FLOW] Microphone not found - showing error dialog');
    setMicrophoneErrorOpen(true);
    setShowCountdown(false);
    setIsRecording(false);
    onMicrophoneNotFound?.();
  };

  const handleStartRecording = () => {
    console.log('useConsultationFlow: Starting NEW consultation - will create analysis');
    resetAllStates();
    setIsNewConsultation(true); // Marcar como nueva consulta
    setShouldCreateAnalysis(true); // NUEVO: debe crear análisis
    // Small delay to ensure states are reset before showing countdown
    setTimeout(() => {
      setShowCountdown(true);
      setIsContinuation(false); // Nueva consulta, no es continuación
    }, 100);
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const handleCountdownComplete = () => {
    console.log('useConsultationFlow: Countdown complete - starting recording');
    setShowCountdown(false);
    setIsRecording(true);
  };

  const handleStopRecording = () => {
    console.log('useConsultationFlow: Stopping recording - showing continue button');
    setIsRecording(false);
    setShowContinueButton(true);
    setIsNewConsultation(false); // Ya no es nueva consulta después de la primera grabación
    setShouldCreateAnalysis(false); // NUEVO: ya no debe crear análisis
  };

  const handleContinueConsultation = () => {
    console.log('useConsultationFlow: CONTINUING consultation - will NOT create analysis');
    console.log('useConsultationFlow: Current analysis ID:', currentAnalysisId);
    setShowContinueButton(false);
    setIsContinuation(true); // Marcar como continuación
    setIsNewConsultation(false); // No es nueva consulta, es continuación
    setShouldCreateAnalysis(false); // CRÍTICO: NO debe crear análisis
    setIsRecording(true);
  };

  const handleFinishConsultation = () => {
    console.log('useConsultationFlow: Finishing consultation');
    resetAllStates();
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const handleUploadFile = () => {
    console.log('useConsultationFlow: Upload file clicked - opening file upload modal');
    setConsultationDialogOpen(false);
    setFileUploadModalOpen(true);
  };

  const handleFileUploadModalChange = (open: boolean) => {
    console.log('useConsultationFlow: File upload modal state changed:', open);
    setFileUploadModalOpen(open);
  };

  const handleFileTranscriptionComplete = (transcriptions: ApiTranscriptionData[], title: string, analysisId: number) => {
    console.log('useConsultationFlow: File transcription completed:', { 
      transcriptionsCount: transcriptions.length, 
      title,
      analysisId
    });
    resetAllStates();
    
    // Navegar al nuevo análisis creado
    navigate(`/dashboard/${analysisId}`);
    
    onFileTranscriptionComplete?.(transcriptions, title);
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const handleRetryMicrophone = () => {
    console.log('🎤 [CONSULTATION_FLOW] Retrying microphone access - restarting flow');
    setMicrophoneErrorOpen(false);
    handleStartRecording();
  };

  const handleMicrophoneErrorChange = (open: boolean) => {
    console.log('🎤 [CONSULTATION_FLOW] Microphone error dialog state changed:', open);
    setMicrophoneErrorOpen(open);
  };

  return {
    showContinueButton,
    consultationDialogOpen,
    showCountdown,
    isRecording,
    countdownKey,
    isContinuation,
    isNewConsultation, // Exponer el nuevo estado
    shouldCreateAnalysis, // NUEVO: exponer flag de creación
    currentAnalysisId, // Exponer el ID del análisis actual
    template,
    microphoneErrorOpen,
    fileUploadModalOpen,
    setConsultationDialogOpen,
    setMicrophoneErrorOpen: handleMicrophoneErrorChange,
    setFileUploadModalOpen: handleFileUploadModalChange,
    handleStartRecording,
    handleCountdownComplete,
    handleStopRecording,
    handleContinueConsultation,
    handleFinishConsultation,
    handleUploadFile,
    handleFileTranscriptionComplete,
    handleMicrophoneNotFound,
    handleRetryMicrophone
  };
};
